import React, { useEffect, useRef, useState } from 'react';
import { BookOpen, Check, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { plainMentions } from './structure/focus';

// Onglet « Règles » : les règles de l'organisation (ex. « Décision réservée »), avec ce qu'elles veulent dire. Les
// redevabilités et les domaines les citent par « @ », comme un rôle ; une règle citée s'ouvre ici.

const when = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
};

const RuleForm = ({ rule = null, onSave, onCancel }) => {
  const [name, setName] = useState(rule?.name || '');
  const [description, setDescription] = useState(rule?.description || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (event) => {
    event.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({ name: name.trim(), description: description.trim() });
    } catch (err) {
      setError(err?.message || 'Enregistrement impossible');
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-2 rounded-xl border border-sky-200 bg-sky-50/40 p-3">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
        placeholder="Nom de la règle, ex. Décision réservée"
        aria-label="Nom de la règle"
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold focus:border-sky-300 focus:outline-none"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={5}
        placeholder="Ce qu'elle veut dire. Ex. Le rôle décide seul de ce qui est réservé : les autres peuvent donner un avis, il tranche et le dit en réunion."
        aria-label="Ce que veut dire la règle"
        className="w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm leading-relaxed focus:border-sky-300 focus:outline-none"
      />
      {error && <p className="text-xs text-rose-600">{error}</p>}
      <div className="flex justify-end gap-1.5">
        <button type="button" onClick={onCancel} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-white">
          Annuler
        </button>
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Enregistrer
        </button>
      </div>
    </form>
  );
};

const Citations = ({ citedBy, circleById, onSelectCircle }) => {
  if (!citedBy?.length) return <p className="text-[11px] text-slate-400">Citée nulle part pour l’instant : « @ » dans une redevabilité ou un domaine.</p>;
  return (
    <ul className="space-y-1">
      {citedBy.map((cite, index) => {
        const circle = circleById.get(Number(cite.circle_id));
        return (
          <li key={`${cite.circle_id}-${cite.field}-${index}`} className="text-[11px] text-slate-500">
            <button
              type="button"
              onClick={() => circle && onSelectCircle && onSelectCircle(circle)}
              disabled={!circle}
              className="font-semibold text-slate-700 hover:text-indigo-700"
            >
              {circle?.name || cite.circle_name}
            </button>
            <span className="text-slate-400"> · {cite.field_label} : </span>
            <span>{plainMentions(cite.text)}</span>
          </li>
        );
      })}
    </ul>
  );
};

const RuleCard = ({ rule, canEdit, highlighted, onUpdate, onDelete, circleById, onSelectCircle }) => {
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (highlighted) ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [highlighted]);
  if (editing) {
    return (
      <li ref={ref}>
        <RuleForm rule={rule} onCancel={() => setEditing(false)}
          onSave={async (fields) => { await onUpdate(rule.id, fields); setEditing(false); }} />
      </li>
    );
  }
  const cited = rule.cited_by?.length || 0;
  const remove = async () => {
    if (!window.confirm(`Supprimer la règle « ${rule.name} » ?`)) return;
    setError(null);
    try {
      await onDelete(rule.id);
    } catch (err) {
      setError(err?.message || 'Suppression impossible');
    }
  };
  return (
    <li
      ref={ref}
      className={`group rounded-xl border p-3 transition ${highlighted ? 'border-sky-300 bg-sky-50/60 ring-2 ring-sky-100' : 'border-slate-200 bg-white'}`}
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5 shrink-0 rounded-md bg-sky-50 px-1.5 text-xs font-bold text-sky-700">§</span>
        <h3 className="min-w-0 flex-1 text-sm font-semibold text-slate-900">{rule.name}</h3>
        {canEdit && (
          <span className="flex shrink-0 gap-0.5 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
            <button type="button" onClick={() => setEditing(true)} title="Modifier" aria-label={`Modifier « ${rule.name} »`}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
              <Pencil size={13} />
            </button>
            <button type="button" onClick={remove} disabled={cited > 0} aria-label={`Supprimer « ${rule.name} »`}
              title={cited > 0 ? 'Encore citée : retire-la d’abord des redevabilités et domaines' : 'Supprimer'}
              className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-30">
              <Trash2 size={13} />
            </button>
          </span>
        )}
      </div>
      <p className={`mt-1.5 whitespace-pre-line pl-7 text-sm leading-relaxed ${rule.description ? 'text-slate-700' : 'italic text-slate-400'}`}>
        {rule.description || 'Ce qu’elle veut dire reste à écrire.'}
      </p>
      <div className="mt-2 space-y-1 border-t border-slate-100 pl-7 pt-2">
        <Citations citedBy={rule.cited_by} circleById={circleById} onSelectCircle={onSelectCircle} />
        <p className="text-[10px] text-slate-400">
          {rule.updated_by_name ? `Modifiée par ${rule.updated_by_name}` : 'Modifiée'} le {when(rule.updated_at)}
        </p>
        {error && <p className="text-[11px] text-rose-600">{error}</p>}
      </div>
    </li>
  );
};

const RulesPanel = ({ rules, canEdit, loading, error, focusRuleId, onCreate, onUpdate, onDelete, circles = [], onSelectCircle }) => {
  const [creating, setCreating] = useState(false);
  const circleById = new Map((circles || []).filter((c) => c?.id).map((c) => [Number(c.id), c]));
  return (
    <div className="flex-1 min-h-0 overflow-y-auto rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800"><BookOpen size={18} /> Règles</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Ce qui vaut pour tous, défini une fois. Une redevabilité ou un domaine la cite avec « @ », comme un rôle.
          </p>
        </div>
        {canEdit && !creating && (
          <button type="button" onClick={() => setCreating(true)}
            className="flex shrink-0 items-center gap-1 rounded-lg bg-sky-50 px-2.5 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100">
            <Plus size={13} /> Nouvelle règle
          </button>
        )}
      </div>
      {!canEdit && (
        <p className="mb-3 text-[11px] text-slate-400">Les règles s’écrivent par un administrateur ou le leader d’un cercle.</p>
      )}
      {error && <p className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</p>}
      {creating && (
        <div className="mb-3">
          <RuleForm onCancel={() => setCreating(false)} onSave={async (fields) => { await onCreate(fields); setCreating(false); }} />
        </div>
      )}
      {loading && !rules.length ? (
        <p className="flex items-center gap-2 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Chargement…</p>
      ) : rules.length ? (
        <ul className="space-y-2">
          {rules.map((rule) => (
            <RuleCard key={rule.id} rule={rule} canEdit={canEdit} highlighted={Number(focusRuleId) === Number(rule.id)}
              onUpdate={onUpdate} onDelete={onDelete} circleById={circleById} onSelectCircle={onSelectCircle} />
          ))}
        </ul>
      ) : !creating && (
        <div className="rounded-xl border border-dashed border-slate-200 p-5 text-center text-sm text-slate-400">
          Aucune règle pour l’instant.
          {canEdit && (
            <button type="button" onClick={() => setCreating(true)} className="mt-2 block w-full text-xs font-semibold text-sky-700 hover:underline">
              Écrire la première (ex. « Décision réservée »)
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default RulesPanel;
